import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    Tooltip,
} from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import {
    FilteredStatuses,
    MeetingServiceOrder,
    MeetingServiceOrderStatus,
    TransactionResult,
} from "../../meetingServiceTypes"
import { StatusItem } from "../StatusMenu"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons"
import { faCircleExclamation } from "@fortawesome/pro-light-svg-icons"
import { Order } from "../../../../../controlKitchenTypes"
import { StandardTwoLinesCell } from "../../../../../../../shared/components/table/StandardTwoLinesCell"

const useStyles = makeStyles()((theme) => ({
    tableContainer: {
        marginBottom: theme.spacing(3),
        marginTop: theme.spacing(2),
    },
    warningIcon: {
        color: theme.palette.tailwind.red[500],
        marginRight: theme.spacing(1),
    },
    orderRow: {
        transition: "background-color .3s linear",
        height: "5rem",
    },
    failedOrderRow: {
        background: theme.palette.tailwind.slate[50],
    },
    warningCell: {
        width: "3%",
        padding: "0 .25rem",
    },
    tableHeader: {
        background: theme.palette.tailwind.slate[50],
    },
    orderStatus: {
        width: "fit-content",
    },
    centeredCell: {
        display: "flex",
        justifyContent: "center",
    },
    tableHeaderCell: {
        color: theme.palette.tailwind.slate[500],
        fontSize: "0.875rem",
    },
    tableDateCell: {
        color: theme.palette.tailwind.slate[700],
        fontSize: "0.875rem",
    },
    columnOrderId: {
        width: "10%",
    },
    columnDeliveryTime: {
        width: "20%",
        whiteSpace: "nowrap",
    },
    columnArrow: {
        width: "3%",
        padding: "0 .25rem",
        textAlign: "center",
    },
    columnStatus: {
        width: "17%",
        whiteSpace: "nowrap",
    },
}))

interface OrdersTableProps {
    orders: MeetingServiceOrder[] | Order[]
    status: FilteredStatuses
    batchResponse?: TransactionResult[]
}

export function OrdersStatusTable({ orders, status, batchResponse }: OrdersTableProps) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()

    return (
        <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
            <Table>
                <TableHead className={classes.tableHeader}>
                    <TableRow>
                        <TableCell className={classes.warningCell} />
                        <TableCell className={cx(classes.tableHeaderCell, classes.columnOrderId)}>
                            <LocalizedStrict id="meeting-service-batch-modal-order-id">
                                <Typography variant="subtitle2" fontWeight="medium">
                                    Order ID
                                </Typography>
                            </LocalizedStrict>
                        </TableCell>
                        <TableCell className={cx(classes.tableHeaderCell, classes.columnDeliveryTime)}>
                            <LocalizedStrict id="meeting-service-batch-modal-delivery-time">
                                <Typography variant="subtitle2" fontWeight="medium">
                                    Delivery time
                                </Typography>
                            </LocalizedStrict>
                        </TableCell>
                        <TableCell className={classes.tableHeaderCell}>
                            <LocalizedStrict id="meeting-service-batch-modal-order-details">
                                <Typography variant="subtitle2" fontWeight="medium">
                                    Order details
                                </Typography>
                            </LocalizedStrict>
                        </TableCell>
                        <TableCell align="center" className={cx(classes.tableHeaderCell, classes.columnStatus)}>
                            <LocalizedStrict id="meeting-service-batch-modal-from">
                                <Typography variant="subtitle2" fontWeight="medium">
                                    From
                                </Typography>
                            </LocalizedStrict>
                        </TableCell>
                        <TableCell className={classes.columnArrow} />
                        <TableCell align="center" className={cx(classes.tableHeaderCell, classes.columnStatus)}>
                            <LocalizedStrict id="meeting-service-batch-modal-to">
                                <Typography variant="subtitle2" fontWeight="medium">
                                    To
                                </Typography>
                            </LocalizedStrict>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {orders.map((order) => {
                        // TODO update when api will be ready
                        const deliveryTime =
                            "deliveryDetails" in order && "time" in order.deliveryDetails
                                ? order.deliveryDetails.time
                                : "deliveries" in order && order.deliveries?.[0]?.deliveryTime
                                  ? order.deliveries[0].deliveryTime
                                  : undefined
                        const dateTime = dateTimeFactory.fromApiOrUndefined(deliveryTime)
                        const transactionOrder = batchResponse?.find(
                            ({ order: batchOrder }) => batchOrder?.id === order.id,
                        )
                        const transactionOrderError =
                            transactionOrder?.status.code !== 200
                                ? transactionOrder?.order?.deliveryDetails?.statusDetails
                                : null
                        const disallowedState =
                            transactionOrderError?.message ?? order.orderAction.statusTransitions?.disallowed?.[status]
                        const allowedState =
                            order.orderAction.statusTransitions?.allowed?.[status] ||
                            Object.values(order.orderAction.statusTransitions?.allowed ?? {})[0]

                        const currentStatus =
                            transactionOrderError?.status ??
                            ("deliveryDetails" in order && "statusDetails" in order.deliveryDetails
                                ? order.deliveryDetails.statusDetails.status
                                : undefined)

                        return (
                            <Tooltip title={allowedState ?? disallowedState}>
                                <TableRow
                                    key={order.id}
                                    className={cx(classes.orderRow, disallowedState && classes.failedOrderRow)}
                                >
                                    <TableCell className={classes.warningCell}>
                                        {disallowedState && (
                                            <FontAwesomeIcon
                                                size="lg"
                                                icon={faCircleExclamation}
                                                className={classes.warningIcon}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell className={cx(classes.tableDateCell, classes.columnOrderId)}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2">{order.id}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell className={classes.columnDeliveryTime}>
                                        <Typography variant="body2" className={classes.tableDateCell}>
                                            {dateTime ? dateTimeFactory.formatDateTime(dateTime) : "-"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <StandardTwoLinesCell
                                            title={
                                                "meetingDetails" in order
                                                    ? (order.meetingDetails?.meetingName ?? "-")
                                                    : order.orderNote || "-"
                                            }
                                            subtitle1={order.customer?.displayName ?? ""}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box className={classes.centeredCell}>
                                            <StatusItem
                                                variant="state"
                                                className={classes.orderStatus}
                                                status={currentStatus ?? MeetingServiceOrderStatus.DEFAULT}
                                            />
                                        </Box>
                                    </TableCell>
                                    <TableCell className={classes.columnArrow}>
                                        <FontAwesomeIcon icon={faArrowRight} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box className={classes.centeredCell}>
                                            <StatusItem
                                                variant="state"
                                                className={classes.orderStatus}
                                                status={status}
                                            />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </Tooltip>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}
